// ** React Imports
import { useState, useEffect, ChangeEvent } from 'react'

// ** Child Imports
import { Switch, Grid, Typography } from '@mui/material'
import Alerts from 'src/components/common/feedback-and-dialog/Alerts'
import SaveNotification from 'src/components/common/SaveNotification'
import LayoutContainer from 'src/components/common/containers/LayoutContainer'
import IntegrationChannels from 'src/components/settings/IntegrationChannels'
import InputTextField from 'src/components/common/form-elements/InputTextField'
import FloCheckbox from 'src/components/common/form-elements/FloCheckbox'

// ** Type Imports
import { DriveMetadataAnalyticsType, DriveMetadataItemType } from 'src/lib/types/integrations'

// ** Context Imports
import { useIntegrations } from 'src/lib/contexts/IntegrationsContext'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import { useTemporary } from 'src/lib/contexts/TemporaryContext'

// ** Util Imports
import { getIntegrations, setAnalyticsConfig, verifyAnalyticsConfig } from 'src/lib/core/api-methods'
import { initialIntegrationsChannelConfig } from 'src/lib/utils/integrationsHelpers'
import { getEnabledChannelsArray } from 'src/lib/utils/parsers'
import { alertSeverites } from 'src/lib/utils/constants'
import useConfirmOnRouteChanges from 'src/lib/utils/custom-hooks/useConfirmOnRouteChanges'

const initialDmdItem: DriveMetadataItemType = {
  clientId: '',
  workspaceId: '',
  appId: '',
  webhookToken: '',
  enabled: true,
  channels: initialIntegrationsChannelConfig
}

const initialDmdState: DriveMetadataAnalyticsType = {
  enabled: false,
  drivemetadata: [initialDmdItem]
}

const DriveMetadata = (): JSX.Element => {
  // Context hooks
  const { setLoaderProperties } = useLoader()
  const { integrations, setIntegrations } = useIntegrations()
  const { temporary, setTemporaryData } = useTemporary()
  const { drivemetadata } = integrations || {}

  // Local State
  const [dmdConfig, setDmdConfig] = useState<DriveMetadataAnalyticsType>(initialDmdState)
  const [openAlert, setOpenAlert] = useState<string>('')
  const [alertMessage, setAlertMessage] = useState<string>('')
  const [settingsUpdated, setSettingsUpdated] = useState<boolean>(false)

  useEffect(() => {
    callGetAnalytics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!temporary?.integrations?.drivemetadata) return
    setDmdConfig(temporary.integrations.drivemetadata)
    setTemporaryData(undefined)
  }, [temporary, setTemporaryData])

  useEffect(() => {
    const contextState = drivemetadata?.drivemetadata?.length > 0 ? drivemetadata : initialDmdState
    setSettingsUpdated(Boolean(JSON.stringify(contextState) !== JSON.stringify(dmdConfig)))
  }, [drivemetadata, dmdConfig])

  useEffect(() => {
    if (!drivemetadata) return
    const hasData = drivemetadata.drivemetadata?.length > 0
    setDmdConfig(hasData ? drivemetadata : initialDmdState)
    setTemporaryData(undefined)
    setSettingsUpdated(false)
  }, [drivemetadata, setTemporaryData])

  useConfirmOnRouteChanges(settingsUpdated, () => {
    return new Promise<boolean>((resolve, reject) => {
      if (confirm('You have unsaved changes. Do you want to stay on the page?')) {
        setTemporaryData({
          integrations: {
            ...integrations,
            drivemetadata: dmdConfig
          }
        })
        resolve(true)
      } else reject(false)
    })
  })

  const callGetAnalytics = async () => {
    try {
      const response: any = await getIntegrations()
      if (response?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, response?.error)
      } else {
        setIntegrations(response?.data)
      }
    } catch (error) {
      handleAlerts(alertSeverites.SEVERITY_ERROR, 'Could not fetch analytics configuration!')
    }
  }

  const isValidForm = () => {
    if (!dmdConfig?.enabled) return true
    const item = dmdConfig.drivemetadata[0]
    if (!item) return false
    if (Boolean(item.enabled) && !Boolean(item.clientId?.trim()?.length)) {
      setOpenAlert('error')
      setAlertMessage('Please enter a valid Client ID!')
      return false
    }
    if (Boolean(item.enabled) && !Boolean(item.workspaceId?.trim()?.length)) {
      setOpenAlert('error')
      setAlertMessage('Please enter a valid Workspace ID!')
      return false
    }
    if (Boolean(item.enabled) && !Boolean(item.appId?.trim()?.length)) {
      setOpenAlert('error')
      setAlertMessage('Please enter a valid App ID!')
      return false
    }
    if (Boolean(item.enabled) && !Boolean(item.webhookToken?.trim()?.length)) {
      setOpenAlert('error')
      setAlertMessage('Please enter a valid Webhook Token!')
      return false
    }
    return true
  }

  const callSubmit = async () => {
    if (!isValidForm()) return
    setLoaderProperties({ isLoaderVisible: true })
    const item = dmdConfig.drivemetadata[0]
    const payload = {
      enabled: dmdConfig.enabled,
      drivemetadata: [
        {
          client_id: item.clientId.trim(),
          workspace_id: item.workspaceId.trim(),
          app_id: item.appId.trim(),
          webhook_token: item.webhookToken.trim(),
          enabled: item.enabled,
          channels: getEnabledChannelsArray(item.channels)
        }
      ]
    }
    try {
      const verifyResponse: any = await verifyAnalyticsConfig('drivemetadata', payload)
      if (verifyResponse?.error) {
        setOpenAlert('error')
        setAlertMessage(verifyResponse?.error)
        console.error('x-trace-id:', verifyResponse.headers?.['x-trace-id'])
        setLoaderProperties({ isLoaderVisible: false })
        return
      }

      const response: any = await setAnalyticsConfig('drivemetadata', payload)
      if (response?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, response?.error)
      } else {
        setIntegrations({ ...integrations, drivemetadata: dmdConfig }, false)
        setOpenAlert('success')
        setAlertMessage('Successfully saved DriveMetadata configuration!')
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errorMessage
        ? error?.response?.data?.errorMessage
        : 'Could not save configuration!'
      handleAlerts(alertSeverites.SEVERITY_ERROR, errorMessage)
      console.error(error)
    }
    setLoaderProperties({ isLoaderVisible: false })
  }

  const handleDiscardSaving = () => {
    if (!drivemetadata) return
    setDmdConfig(drivemetadata.drivemetadata?.length > 0 ? drivemetadata : initialDmdState)
    setSettingsUpdated(false)
    setTemporaryData(undefined)
  }

  const handleAlerts = (severity: string, message?: string) => {
    setOpenAlert(severity)
    message && setAlertMessage(message)
  }

  const handleCloseAlert = (event: any, reason: any) => {
    if (reason === 'clickaway') {
      return
    }
    handleAlerts('')
  }

  const updateItem = (field: keyof DriveMetadataItemType, value: string | boolean) => {
    const updated = { ...dmdConfig }
    updated.drivemetadata = [{ ...updated.drivemetadata[0], [field]: value }]
    setDmdConfig(updated)
  }

  const item = dmdConfig.drivemetadata[0] ?? initialDmdItem

  return (
    <>
      <LayoutContainer>
        <Grid display={'flex'} flexDirection={'column'} sx={{ gap: 2, width: '100%' }}>
          <Grid item xs={12} md={12} display={'flex'} alignItems={'center'}>
            <Grid item xs={10} md={11} display={'flex'} alignItems={'center'}>
              <Typography variant='body2'>DriveMetadata integration is</Typography>
              <Typography variant='body1' sx={{ fontWeight: 500, marginLeft: 2 }}>
                {`${dmdConfig?.enabled ? 'enabled' : 'disabled'}`}
              </Typography>
            </Grid>
            <Grid item xs={2} md={1} display={'flex'} alignItems={'center'} justifyContent={'end'}>
              <Switch
                name='dmdToggle'
                checked={dmdConfig?.enabled}
                inputProps={{ 'aria-label': 'controlled' }}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const checked = event?.target?.checked
                  setDmdConfig({ ...dmdConfig, enabled: checked })
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </LayoutContainer>

      {Boolean(dmdConfig?.enabled) && (
        <IntegrationChannels
          channels={item.channels ?? []}
          onChange={(channels: any) => {
            const updated = { ...dmdConfig }
            updated.drivemetadata = [{ ...updated.drivemetadata[0], channels }]
            setDmdConfig(updated)
          }}
          hideChannels={!dmdConfig?.enabled}
        >
          <Grid display={'flex'} flexDirection={'column'} sx={{ gap: 2, width: '100%' }}>
            <div className='flex justify-start gap-2'>
              <div className='flex items-center justify-between w-full'>
                <Typography variant='body1' className='min-w-fit' sx={{ fontWeight: 600 }}>
                  DriveMetadata Configuration
                </Typography>
              </div>
            </div>

            <Grid item xs={12} md={12} display={'flex'} flexDirection={'column'} justifyContent={'space-between'}>
              <Grid display={'flex'} alignItems={'center'} gap={19}>
                <FloCheckbox
                  value={item.enabled}
                  label='Enable this configuration'
                  setValue={(checked: boolean) => updateItem('enabled', checked)}
                />
              </Grid>
            </Grid>

            {Boolean(item.enabled) && (
              <>
                <Grid item xs={12} md={12} display={'flex'} flexDirection={'column'} justifyContent={'space-between'}>
                  <Grid display={'flex'} alignItems={'center'} gap={19}>
                    <InputTextField
                      textFieldId='dmd-client-id'
                      label='Client ID'
                      inputPlaceholder='Enter your Client ID'
                      inputProps={{ 'aria-label': 'controlled' }}
                      inputValue={item.clientId}
                      handleChange={(event: ChangeEvent<HTMLInputElement>) => {
                        updateItem('clientId', event?.target?.value)
                      }}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12} md={12} display={'flex'} flexDirection={'column'} justifyContent={'space-between'}>
                  <Grid display={'flex'} alignItems={'center'} gap={19}>
                    <InputTextField
                      textFieldId='dmd-workspace-id'
                      label='Workspace ID'
                      inputPlaceholder='Enter your Workspace ID'
                      inputProps={{ 'aria-label': 'controlled' }}
                      inputValue={item.workspaceId}
                      handleChange={(event: ChangeEvent<HTMLInputElement>) => {
                        updateItem('workspaceId', event?.target?.value)
                      }}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12} md={12} display={'flex'} flexDirection={'column'} justifyContent={'space-between'}>
                  <Grid display={'flex'} alignItems={'center'} gap={19}>
                    <InputTextField
                      textFieldId='dmd-app-id'
                      label='App ID'
                      inputPlaceholder='Enter your App ID'
                      inputProps={{ 'aria-label': 'controlled' }}
                      inputValue={item.appId}
                      handleChange={(event: ChangeEvent<HTMLInputElement>) => {
                        updateItem('appId', event?.target?.value)
                      }}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12} md={12} display={'flex'} flexDirection={'column'} justifyContent={'space-between'}>
                  <Grid display={'flex'} alignItems={'center'} gap={19}>
                    <InputTextField
                      textFieldId='dmd-webhook-token'
                      label='Webhook Token'
                      inputPlaceholder='Enter your Webhook Token'
                      inputProps={{ 'aria-label': 'controlled' }}
                      inputValue={item.webhookToken}
                      handleChange={(event: ChangeEvent<HTMLInputElement>) => {
                        updateItem('webhookToken', event?.target?.value)
                      }}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </IntegrationChannels>
      )}

      <SaveNotification onSave={callSubmit} onDiscard={handleDiscardSaving} isVisible={settingsUpdated} />

      {openAlert.length ? (
        <Alerts
          open={true}
          duration={2000}
          closeHandler={handleCloseAlert}
          severity={openAlert}
          message={alertMessage}
        />
      ) : null}
    </>
  )
}
export default DriveMetadata
